import React, { useState, useMemo, useContext } from "react";
import useDashboardAnalytics from "../../services/Dashboard/useDashboardAnalytics";

import {
  BarChart3,
  PieChart as PieChartIcon,
  Download,
  IndianRupee,
  ShoppingCart,
  Crown,
  RefreshCcw,
  PackageSearch,
  List,
} from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from "recharts";
import { GlobalContext } from "../../Context/GlobalContext";
import { exportAnyJsonToExcel } from "../../utils/exportAnyJsonToExcel";

const COLORS = ["#6366F1", "#22C55E", "#F59E0B", "#EF4444", "#06B6D4"];

const currency = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const getToday = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60 * 1000;

  return new Date(now.getTime() - offset).toISOString().split("T")[0];
};

const getMostSoldName = (item) =>
  item?.product_name ||
  item?.productName ||
  item?.item_name ||
  item?.itemName ||
  item?.name ||
  "-";

const getMostSoldQuantity = (item) =>
  Number(
    item?.total_quantity_sold ??
      item?.soldQuantity ??
      item?.sold_quantity ??
      item?.totalQuantity ??
      item?.total_quantity ??
      item?.quantity ??
      item?.qty ??
      0,
  );

const getMostSoldRevenue = (item) =>
  Number(
    item?.total_item_price ??
      item?.totalPrice ??
      item?.total_price ??
      item?.totalAmount ??
      item?.total_amount ??
      item?.revenue ??
      item?.totalSales ??
      item?.total_sales ??
      0,
  );

const REPORT_TABS = [
  {
    key: "bestSelling",
    label: "Best Selling",
    icon: BarChart3,
  },
  {
    key: "mostSold",
    label: "Most Sold Items",
    icon: List,
  },
];

const Reports = () => {
  const [activeTab, setActiveTab] = useState("bestSelling");

  const [platform, setPlatform] = useState("whatsapp");
  const [interval, setInterval] = useState("30");

  const [mostSoldPlatform, setMostSoldPlatform] = useState("all");
  const [mostSoldDate, setMostSoldDate] = useState(getToday);

  const [refreshKey, setRefreshKey] = useState(0);

  const { userID } = useContext(GlobalContext);

  const {
    topOrdersAnalytics: finalData = [],
    mostSoldItems = [],
    mostSoldLoading,
    loading,
  } = useDashboardAnalytics(
    userID,
    interval,
    platform,
    {
      enableDashboard: false,
      enableOrderAnalytics: false,
      enableBestSelling: activeTab === "bestSelling",
      enableMostSold: activeTab === "mostSold",
      mostSoldDate,
      mostSoldPlatform: mostSoldPlatform === "all" ? null : mostSoldPlatform,
    },
    refreshKey,
  );

  const currentLoading = activeTab === "mostSold" ? mostSoldLoading : loading;

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const totals = useMemo(() => {
    const revenue = finalData.reduce(
      (sum, item) => sum + Number(item.totalPrice || 0),
      0,
    );

    const orders = finalData.reduce(
      (sum, item) => sum + Number(item.orderCount || 0),
      0,
    );

    const top = [...finalData].sort(
      (a, b) => Number(b.orderCount) - Number(a.orderCount),
    )[0];

    return { revenue, orders, top };
  }, [finalData]);

  const mostSoldTotals = useMemo(() => {
    const totalSold = mostSoldItems.reduce(
      (sum, item) => sum + getMostSoldQuantity(item),
      0,
    );

    const totalRevenue = mostSoldItems.reduce(
      (sum, item) => sum + getMostSoldRevenue(item),
      0,
    );

    const top = [...mostSoldItems].sort(
      (a, b) => getMostSoldQuantity(b) - getMostSoldQuantity(a),
    )[0];

    return {
      totalSold,
      totalRevenue,
      itemCount: mostSoldItems.length,
      top,
    };
  }, [mostSoldItems]);

  const chartData = finalData.map((item) => ({
    ...item,
    orderCount: Number(item.orderCount),
    totalPrice: Number(item.totalPrice),
  }));

  const exportBestSellingExcel = () => {
    const cleanData = finalData.map((item) => ({
      Product: item.productName,
      Orders: Number(item.orderCount),
      Revenue: Number(item.totalPrice),
    }));

    exportAnyJsonToExcel({
      data: cleanData,
      filePrefix: "TopBestSellingOrders",
      sheetName: "OrdersReport",
    });
  };

  const exportMostSoldExcel = () => {
    const cleanData = mostSoldItems.map((item) => ({
      Product: getMostSoldName(item),
      "Total Quantity Sold": getMostSoldQuantity(item),
      "Total Item Price": getMostSoldRevenue(item),
      Date: mostSoldDate,
    }));

    exportAnyJsonToExcel({
      data: cleanData,
      filePrefix: "MostSoldItems",
      sheetName: "MostSoldItems",
    });
  };

  const exportExcel =
    activeTab === "mostSold" ? exportMostSoldExcel : exportBestSellingExcel;

  const noData = !loading && finalData.length === 0;
  const noMostSoldData = !mostSoldLoading && mostSoldItems.length === 0;

  return (
    <>
      <div className="p-6 space-y-6 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
        <div className="bg-white border rounded-lg p-4 flex-shrink-0">
          <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-5 mb-4">
            <div className="min-w-0">
              <div className="text-xl font-semibold text-gray-800">
                Reports And Analytics
              </div>

              <div className="mt-4 border-b border-gray-200 overflow-x-auto">
                <div className="flex items-center gap-6 min-w-max">
                  {REPORT_TABS.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.key;

                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={`inline-flex items-center gap-2 pb-3 px-0 border-b-2 text-[15px] font-medium whitespace-nowrap transition-all ${
                          isActive
                            ? "border-green-600 text-green-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        <Icon size={16} strokeWidth={2} />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-4 montserrat-font-1">
              {activeTab === "bestSelling" ? (
                <>
                  <div className="flex flex-col text-xs text-gray-600">
                    <label className="mb-1">Platform</label>

                    <select
                      value={platform}
                      onChange={(e) => setPlatform(e.target.value)}
                      className="border rounded-md px-3 py-1.5 text-sm bg-white"
                    >
                      <option value="whatsapp">WhatsApp</option>
                      <option value="pos">POS</option>
                    </select>
                  </div>

                  <div className="flex flex-col text-xs text-gray-600">
                    <label className="mb-1">Interval</label>

                    <select
                      value={interval}
                      onChange={(e) => setInterval(e.target.value)}
                      className="border rounded-md px-3 py-1.5 text-sm bg-white"
                    >
                      <option value="7">7 days</option>
                      <option value="30">30 days</option>
                      <option value="90">90 days</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-col text-xs text-gray-600">
                    <label className="mb-1">Platform</label>

                    <select
                      value={mostSoldPlatform}
                      onChange={(e) => setMostSoldPlatform(e.target.value)}
                      className="border rounded-md px-3 py-1.5 text-sm bg-white"
                    >
                      <option value="all">All</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="pos">POS</option>
                    </select>
                  </div>

                  <div className="flex flex-col text-xs text-gray-600">
                    <label className="mb-1">Date</label>

                    <input
                      type="date"
                      value={mostSoldDate}
                      onChange={(e) => setMostSoldDate(e.target.value)}
                      className="border rounded-md px-3 py-1.5 text-sm bg-white"
                    />
                  </div>
                </>
              )}

              <button
                onClick={exportExcel}
                disabled={
                  activeTab === "mostSold"
                    ? mostSoldItems.length === 0
                    : finalData.length === 0
                }
                className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={16} />
                Export
              </button>

              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-green-700"
              >
                <span className={currentLoading ? "animate-spin" : ""}>
                  <RefreshCcw size={14} />
                </span>
                Refresh
              </button>
            </div>
          </div>

          {activeTab === "bestSelling" ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 size={20} className="text-blue-600" />
                <h5 className="font-bold text-gray-800">
                  Top Best-Selling Orders
                </h5>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mt-5 mb-3">
                <div className="bg-indigo-50 rounded-xl p-4">
                  <div className="flex justify-between text-indigo-600 text-sm">
                    Total Revenue
                    <IndianRupee size={18} />
                  </div>
                  <div className="font-bold text-lg">
                    {currency(totals.revenue)}
                  </div>
                </div>

                <div className="bg-emerald-50 rounded-xl p-4">
                  <div className="flex justify-between text-emerald-600 text-sm">
                    Total Orders
                    <ShoppingCart size={18} />
                  </div>
                  <div className="font-bold text-lg">{totals.orders}</div>
                </div>

                <div className="bg-orange-50 rounded-xl p-4">
                  <div className="flex justify-between text-orange-600 text-sm">
                    Top Product
                    <Crown size={18} />
                  </div>
                  <div className="font-bold text-lg truncate">
                    {totals.top?.productName || "-"}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2">
                <PackageSearch size={20} className="text-blue-600" />
                <h5 className="font-bold text-gray-800">Most Sold Items</h5>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mt-5 mb-3">
                <div className="bg-indigo-50 rounded-xl p-4">
                  <div className="flex justify-between text-indigo-600 text-sm">
                    Total Quantity Sold
                    <ShoppingCart size={18} />
                  </div>
                  <div className="font-bold text-lg">
                    {mostSoldTotals.totalSold}
                  </div>
                </div>

                <div className="bg-emerald-50 rounded-xl p-4">
                  <div className="flex justify-between text-emerald-600 text-sm">
                    Total Item Price
                    <IndianRupee size={18} />
                  </div>
                  <div className="font-bold text-lg">
                    {currency(mostSoldTotals.totalRevenue)}
                  </div>
                </div>

                <div className="bg-orange-50 rounded-xl p-4">
                  <div className="flex justify-between text-orange-600 text-sm">
                    Most Sold Item
                    <Crown size={18} />
                  </div>
                  <div className="font-bold text-lg truncate">
                    {mostSoldTotals.top
                      ? getMostSoldName(mostSoldTotals.top)
                      : "-"}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-6 pr-2">
          {activeTab === "bestSelling" ? (
            <>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border rounded-xl p-4">
                  <div className="flex items-center gap-2 text-blue-600 font-semibold text-sm mb-3">
                    <BarChart3 size={16} />
                    <span>Orders by Product</span>
                  </div>

                  <div className="h-[240px]">
                    {loading ? (
                      <div className="flex items-center justify-center h-full">
                        Loading...
                      </div>
                    ) : noData ? (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        No data
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                          <XAxis
                            dataKey="productName"
                            tick={{ fontSize: 11 }}
                            angle={-25}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="orderCount" radius={[6, 6, 0, 0]}>
                            <LabelList
                              dataKey="orderCount"
                              position="top"
                              style={{ fontSize: 12 }}
                            />
                            {chartData.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                <div className="bg-white border rounded-xl p-4">
                  <div className="flex items-center gap-2 text-green-600 font-semibold text-sm mb-3">
                    <PieChartIcon size={16} />
                    <span>Revenue Share</span>
                  </div>

                  <div className="flex gap-4 h-[240px]">
                    <div className="w-1/2">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={chartData}
                            dataKey="totalPrice"
                            nameKey="productName"
                            innerRadius={50}
                            outerRadius={80}
                          >
                            {chartData.map((_, i) => (
                              <Cell key={i} fill={COLORS[i % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="w-1/2 overflow-y-auto pr-2">
                      <div className="space-y-2 text-sm">
                        {chartData.map((item, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between border-b pb-1"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className="w-3 h-3 rounded-sm flex-shrink-0"
                                style={{
                                  backgroundColor: COLORS[i % COLORS.length],
                                }}
                              />
                              <span className="text-gray-700 truncate">
                                {item.productName}
                              </span>
                            </div>
                            <span className="font-medium text-gray-600 ml-2">
                              ₹{Number(item.totalPrice)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border rounded-lg h-full overflow-hidden">
                <div className="h-full overflow-auto">
                  <div className="sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-green-100 border-b">
                    <span className="font-semibold text-gray-700">
                      Detailed Report
                    </span>
                    <button
                      onClick={exportBestSellingExcel}
                      className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-blue-700"
                    >
                      <Download size={16} />
                      Export
                    </button>
                  </div>

                  <div>
                    <table className="w-full text-sm">
                      <thead className="sticky top-[52px] z-20 text-gray-600">
                        <tr>
                          <th className="bg-green-50 text-left px-4 py-3">
                            Image
                          </th>
                          <th className="bg-green-50 text-left px-4 py-3">
                            Product
                          </th>
                          <th className="bg-green-50 text-right px-4 py-3">
                            Orders
                          </th>
                          <th className="bg-green-50 text-right px-4 py-3">
                            Revenue
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {finalData.map((item) => (
                          <tr
                            key={item.productId}
                            className="border-t hover:bg-green-50"
                          >
                            <td className="px-4 py-3">
                              <img
                                src={item.productImage || "/no-image.png"}
                                alt={item.productName || "Product"}
                                className="w-10 h-10 rounded object-cover"
                              />
                            </td>
                            <td className="px-4 py-3 font-medium">
                              {item.productName}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {Number(item.orderCount)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {currency(Number(item.totalPrice))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white border rounded-lg min-h-[420px] overflow-hidden">
              <div className="h-full overflow-auto">
                <div className="sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-blue-100 border-b">
                  <div>
                    <span className="font-semibold text-gray-700">
                      Most Sold Items Report
                    </span>
                    <span className="ml-2 text-xs text-gray-500">
                      {mostSoldDate}
                    </span>
                  </div>

                  <button
                    onClick={exportMostSoldExcel}
                    disabled={mostSoldItems.length === 0}
                    className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download size={16} />
                    Export
                  </button>
                </div>

                {mostSoldLoading ? (
                  <div className="min-h-[320px] flex items-center justify-center text-gray-500">
                    Loading most sold items...
                  </div>
                ) : noMostSoldData ? (
                  <div className="min-h-[320px] flex items-center justify-center text-gray-400">
                    No data found for selected date/platform
                  </div> 
                ) : (
                  <table className="w-full text-sm">
                    <thead className="sticky top-[52px] z-20 text-gray-600">
                      <tr>
                        <th className="bg-blue-50 text-left px-4 py-3">
                          Product
                        </th>
                        <th className="bg-blue-50 text-right px-4 py-3">
                          Total Quantity Sold
                        </th>
                        <th className="bg-blue-50 text-right px-4 py-3">
                          Total Item Price
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {mostSoldItems.map((item, index) => {
                        const itemName = getMostSoldName(item);

                        return (
                          <tr
                            key={
                              item?.product_id ||
                              item?.product_name ||
                              `${itemName}-${index}`
                            }
                            className="border-t hover:bg-blue-50"
                          >
                            <td className="px-4 py-3 font-medium">
                              {itemName}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold">
                              {getMostSoldQuantity(item)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {currency(getMostSoldRevenue(item))}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Reports;
